import React, { useState, useEffect } from 'react';
import {
  Bot,
  MessageSquare,
  Users,
  TrendingUp,
  ArrowRight,
  Zap,
  Globe,
  Calendar,
  Sparkles,
  ChevronRight,
  Plus
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { api } from '../../lib/api';
import { useLanguage } from '../../context/LanguageContext';

const StatCard = ({ title, value, icon: Icon, color, link }) => (
  <Link to={link}>
    <Card className="hover:shadow-lg transition-all cursor-pointer border-slate-200 group">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
          </div>
          <div className={`w-12 h-12 ${color} rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const LaunchpadItem = ({ icon: Icon, title, description, link, cta, color }) => (
  <Card className="border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
    <CardContent className="p-6">
      <div className="flex gap-4">
        <div className={`w-12 h-12 rounded-xl ${color} flex-shrink-0 flex items-center justify-center text-white`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">{description}</p>
          <Link to={link}>
            <Button variant="outline" size="sm" className="w-full justify-between items-center text-blue-600 border-blue-100 hover:bg-blue-50 group">
              {cta}
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardHome = () => {
  const { t } = useLanguage();
  const [botsCount, setBotsCount] = useState(0);
  const [conversationsCount, setConversationsCount] = useState(0);
  const [contactsCount, setContactsCount] = useState(0);
  const [messagesCount, setMessagesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/api/analytics/dashboard');
      if (response.ok) {
        const data = await response.json();
        setBotsCount(data.bots_count || 0);
        setConversationsCount(data.conversations_count || 0);
        setContactsCount(data.contacts_count || 0);
        setMessagesCount(data.messages_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const displayValue = (val) => loading ? '-' : val;

  return (
    <div className="max-w-7xl mx-auto space-y-8 p-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{t('dashboard.welcome')}</h1>
          <p className="text-slate-500 mt-1">{t('dashboard.summary')}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/dashboard/templates">
            <Button variant="outline" className="border-slate-200">
              <Sparkles className="h-4 w-4 mr-2 text-blue-600" />
              {t('dashboard.exploreTemplates')}
            </Button>
          </Link>
          <Link to="/dashboard/bots">
            <Button className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200">
              <Plus className="h-4 w-4 mr-2" />
              {t('dashboard.createNewBot')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title={t('dashboard.myBots')} value={displayValue(botsCount)} icon={Bot} color="bg-blue-600" link="/dashboard/bots" />
        <StatCard title={t('dashboard.activeConversations')} value={displayValue(conversationsCount)} icon={MessageSquare} color="bg-green-500" link="/dashboard/conversations" />
        <StatCard title={t('dashboard.totalContacts')} value={displayValue(contactsCount)} icon={Users} color="bg-purple-500" link="/dashboard/contacts" />
        <StatCard title={t('dashboard.messages')} value={displayValue(messagesCount)} icon={TrendingUp} color="bg-orange-500" link="/dashboard/conversations" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Launchpad Column */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-xl font-bold text-slate-900">{t('dashboard.quickSetup')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <LaunchpadItem
              icon={Globe}
              title={t('dashboard.bookingPage')}
              description={t('dashboard.bookingPageDesc')}
              link="/dashboard/website"
              cta={t('dashboard.setupSite')}
              color="bg-indigo-500"
            />
            <LaunchpadItem
              icon={Zap}
              title={t('dashboard.aiAutomation')}
              description={t('dashboard.aiAutomationDesc')}
              link="/dashboard/automations"
              cta={t('dashboard.activateAI')}
              color="bg-orange-500"
            />
            <LaunchpadItem
              icon={Calendar}
              title={t('dashboard.calendar')}
              description={t('dashboard.calendarDesc')}
              link="/dashboard/calendar"
              cta={t('dashboard.viewCalendar')}
              color="bg-blue-500"
            />
            <LaunchpadItem
              icon={Users}
              title={t('dashboard.importContacts')}
              description={t('dashboard.importContactsDesc')}
              link="/dashboard/contacts"
              cta={t('dashboard.uploadCSV')}
              color="bg-emerald-500"
            />
          </div>
        </div>

        {/* Sidebar Tips */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-slate-900">{t('dashboard.growthTips')}</h2>
          <Card className="bg-gradient-to-br from-blue-600 to-blue-800 text-white border-none shadow-xl shadow-blue-200 overflow-hidden relative">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Sparkles className="h-24 w-24" />
            </div>
            <CardContent className="p-6 relative">
              <h3 className="font-bold text-lg mb-2">{t('dashboard.boostBookings')}</h3>
              <p className="text-blue-100 text-sm mb-6">
                {t('dashboard.boostBookingsDesc')}
              </p>
              <Link to="/dashboard/widget">
                <Button className="w-full bg-white text-blue-600 hover:bg-blue-50 font-bold">
                  {t('dashboard.getQR')}
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">{t('dashboard.connectionStatus')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium text-slate-700">WhatsApp API</span>
                </div>
                <span className="text-xs text-slate-400 font-bold uppercase">{t('common.active')}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <span className="text-sm font-medium text-slate-700">Instagram DM</span>
                </div>
                <span className="text-xs text-slate-400 font-bold uppercase underline cursor-pointer hover:text-blue-500">{t('common.link')}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
